import React, { useCallback, useState, useEffect, useRef } from 'react';
import Calendar from 'react-calendar';
import PropTypes from 'prop-types';
import { useLocale } from 'next-intl';

import StyledCalendarFilter from './calendarFilter.style';
import { ArrowRight, ArrowLeft } from '../../icons/arrow/arrow';

const CalendarFilter = ({ activeDates, filterChange, initialDate = new Date() }) => {
  const locale = useLocale();
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [previousMonthAvailable, setPreviousMonthAvailable] = useState(false);
  const ref = useRef();

  const isCurrentMonth = date => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth(); // Get the month of the current date (0-11)
    const currentYear = currentDate.getFullYear(); // Get the year of the current date

    const selectedMonth = date.getMonth(); // Get the month of the selected date (0-11)
    const selectedYear = date.getFullYear(); // Get the year of the selected date

    return selectedMonth === currentMonth && selectedYear === currentYear;
  };

  useEffect(() => {
    setSelectedDate(initialDate);
    setPreviousMonthAvailable(!isCurrentMonth(initialDate));
  }, [initialDate]);

  const isSameDay = (date1, date2) => {
    return (
      date1.getDate() === date2.getDate() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getFullYear() === date2.getFullYear()
    );
  };

  const tileClassName = useCallback(
    date => {
      if (
        activeDates?.find(dDate => {
          return isSameDay(dDate, date.date);
        })
      ) {
        return 'calendarfilter__eventondate';
      }
      return 'calendarfilter__nodate';
    },
    [activeDates]
  );

  return (
    <StyledCalendarFilter>
      <Calendar
        inputRef={ref}
        locale={locale}
        maxDetail="month"
        minDetail="month"
        defaultView="month"
        value={selectedDate}
        activeStartDate={selectedDate}
        onActiveStartDateChange={(value, event) => {
          filterChange(value.activeStartDate, 'month');
          setPreviousMonthAvailable(!isCurrentMonth(value.activeStartDate));
        }}
        onChange={(value, event) => {
          setSelectedDate(value);
          filterChange(value, 'date');
          setPreviousMonthAvailable(!isCurrentMonth(selectedDate));
        }}
        tileClassName={tileClassName}
        next2Label={null}
        prev2Label={null}
        nextLabel={<ArrowRight className="calendarFilter__arrow" />}
        prevLabel={!previousMonthAvailable ? null : <ArrowLeft className="calendarFilter__arrow" />}
        navigationLabel={({ date }) =>
          `${date.toLocaleDateString(locale, { month: 'long' }).toUpperCase()}`
        }
        formatShortWeekday={(locale, date) =>
          `${date.toLocaleDateString(locale, { weekday: 'short' })}`
        }
        tileContent={({ view, date }) =>
          view === 'month' ? (
            <p
              aria-label={date.getDate()}
              className={`calendarfilter__bg ${isSameDay(selectedDate, date) ? ' selected' : ''}`}
            >
              {date.getDate()}
            </p>
          ) : null
        }
      />
    </StyledCalendarFilter>
  );
};

CalendarFilter.propTypes = {
  activeDates: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  filterChange: PropTypes.func.isRequired,
  initialDate: PropTypes.instanceOf(Date)
};

export default CalendarFilter;
